import { useState, useEffect, useCallback, useRef } from "react";

export interface FilterState {
  searchQuery: string;
  activeCategory: string;
  activeLocation: string;
  jobType: string;
  sortBy: string;
}

export interface SavedFilter {
  id: string;
  name: string;
  filters: FilterState;
  createdAt: number;
}

const LAST_FILTER_KEY = "job_search_last_filter";
const PRESETS_KEY = "job_search_saved_presets";

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function readLastFilter(): FilterState | null {
  try {
    const raw = localStorage.getItem(LAST_FILTER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FilterState;
    if (typeof parsed.searchQuery === "string" && typeof parsed.sortBy === "string") {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeLastFilter(filters: FilterState): void {
  try {
    localStorage.setItem(LAST_FILTER_KEY, JSON.stringify(filters));
  } catch {
    // localStorage might be unavailable
  }
}

function readPresets(): SavedFilter[] {
  try {
    const raw = localStorage.getItem(PRESETS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as SavedFilter[];
    return [];
  } catch {
    return [];
  }
}

function writePresets(presets: SavedFilter[]): void {
  try {
    localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
  } catch {
    // localStorage might be unavailable
  }
}

/**
 * Check if two filter states are effectively the same
 * (ignoring irrelevant differences)
 */
function filtersEqual(a: FilterState, b: FilterState): boolean {
  return (
    a.searchQuery === b.searchQuery &&
    a.activeCategory === b.activeCategory &&
    a.activeLocation === b.activeLocation &&
    a.jobType === b.jobType &&
    a.sortBy === b.sortBy
  );
}

/**
 * Check if filters are "empty" (no active filtering beyond defaults)
 */
function isFilterEmpty(filters: FilterState): boolean {
  return (
    filters.searchQuery === "" &&
    filters.activeCategory === "" &&
    filters.activeLocation === "" &&
    filters.jobType === "" &&
    filters.sortBy === "newest"
  );
}

/**
 * Generate a descriptive label from filter state
 */
function generateFilterLabel(filters: FilterState): string {
  const parts: string[] = [];
  if (filters.searchQuery) parts.push(`"${filters.searchQuery}"`);
  if (filters.activeCategory) parts.push(filters.activeCategory.split(",")[0].trim());
  if (filters.activeLocation) {
    const locMap: Record<string, string> = {
      "hà nội": "Hà Nội",
      "tp. hồ chí minh": "HCM",
      "đà nẵng": "Đà Nẵng",
      "hải phòng": "Hải Phòng",
      "cần thơ": "Cần Thơ",
    };
    parts.push(locMap[filters.activeLocation] || filters.activeLocation);
  }
  if (filters.jobType) {
    const typeLabels: Record<string, string> = {
      "full-time": "Full-time",
      "part-time": "Part-time",
      contract: "Hợp đồng",
    };
    parts.push(typeLabels[filters.jobType] || filters.jobType);
  }
  return parts.join(" · ") || "Tất cả";
}

export function useFilterPersistence() {
  const [presets, setPresets] = useState<SavedFilter[]>([]);
  const [initialFilter, setInitialFilter] = useState<FilterState | null>(null);
  const [initialized, setInitialized] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load on mount
  useEffect(() => {
    const saved = readLastFilter();
    const savedPresets = readPresets();
    setPresets(savedPresets);
    if (saved && !isFilterEmpty(saved)) {
      setInitialFilter(saved);
    }
    setInitialized(true);
  }, []);

  // Auto-save current filters (debounced 800ms)
  const persistFilter = useCallback((filters: FilterState) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      writeLastFilter(filters);
    }, 800);
  }, []);

  // Save a named preset
  const savePreset = useCallback(
    (name: string, filters: FilterState): SavedFilter => {
      const newPreset: SavedFilter = {
        id: generateId(),
        name: name.trim() || generateFilterLabel(filters),
        filters: { ...filters },
        createdAt: Date.now(),
      };
      const updated = [newPreset, ...presets];
      setPresets(updated);
      writePresets(updated);
      return newPreset;
    },
    [presets],
  );

  // Delete a preset by id
  const deletePreset = useCallback(
    (id: string) => {
      const updated = presets.filter((p) => p.id !== id);
      setPresets(updated);
      writePresets(updated);
    },
    [presets],
  );

  // Check if current filters match a saved preset
  const findMatchingPreset = useCallback(
    (filters: FilterState): SavedFilter | null => {
      return presets.find((p) => filtersEqual(p.filters, filters)) || null;
    },
    [presets],
  );

  // Generate label helper
  const getFilterLabel = useCallback((filters: FilterState): string => {
    return generateFilterLabel(filters);
  }, []);

  // Cleanup timer
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return {
    initialFilter,
    initialized,
    presets,
    persistFilter,
    savePreset,
    deletePreset,
    findMatchingPreset,
    getFilterLabel,
    isFilterEmpty,
  };
}